import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import { mergeLeft } from 'ramda'
import type { Catalog, DynamicFeatures } from 'api-client/keystone.model'
import type { BlueprintTypes } from 'app/plugins/openstack/components/infrastructure/blueprint/model'

export interface SessionFeatures {
  active_nav_background?: string
  active_nav_left_border?: string
  active_nav_text_color?: string
  branding_name?: string
  browser_title?: string
  custom_tour_link?: string
  customer_metadata?: Record<string, any>
  customer_tier?: string
  developer_mode?: boolean
  experimental?: {
    aws?: boolean
    boot_from_volume?: boolean
    churnZero?: boolean
    cinder?: boolean
    cloud_init?: boolean
    containervisor?: boolean
    designate?: boolean
    docker?: boolean
    e2etest?: boolean
    edit_images?: boolean
    forced_cloud_init?: boolean
    guided_tour?: boolean
    heat?: boolean
    hidePrebuiltApps?: boolean
    high_availability?: boolean
    hypervisor?: boolean
    images_public_catalog?: boolean
    images_url_import?: boolean
    instance_resize?: boolean
    intercom?: boolean
    intercom_admin_only?: boolean
    kube2go?: boolean
    leases?: boolean
    liftoff?: boolean
    live_migration?: boolean
    mfa?: boolean
    mixpanel?: boolean
    nano?: boolean
    nano_aggregate_max?: number
    nano_max?: number
    networking_plugin?: string
    neutron?: boolean
    notifications?: boolean
    novnc?: boolean
    openstackEnabled?: boolean
    port_security?: boolean
    sandbox?: boolean
    soft_delete?: boolean
    sso?: boolean
    ubuntu_agent_installer?: boolean
    vmw_gateway?: boolean
    kplane?: boolean
    managed_apps?: boolean
    product_type?: string
    pmov2_du_type?: string
    [key: string]: any
  }
  features?: DynamicFeatures
  header_background_color?: string
  header_border?: boolean
  header_border_color?: string
  header_text_color?: string
  inactive_nav_background?: string
  inactive_nav_text_color?: string
  intercom_id?: string
  login_button_color?: string
  login_button_text_color?: string
  login_header_background_color?: string
  nav_background?: string
  releaseVersion?: string
  shortname?: string
  sso_login_url?: string
  support_link?: string
  support_request_endpoint?: string
  sso_infra_url?: string
  onprem_pcdctl_version?: string
  kaapi?: {
    enable_kaapi?: boolean
    enable_openstack_storage?: boolean
    enable_openstack_networks?: boolean
    enable_control_plane_class?: boolean
    enable_private_registry?: boolean
  }
}

export interface SessionState {
  unscopedToken: string
  scopedToken: string
  username: string
  expiresAt: string
  userDetails: { [key: string]: any }
  features: SessionFeatures
  isSsoToken: boolean
  isSystemUser?: boolean // Left as ? because too many ts errors
  onboardingRedirectToUrl: string
  activeRegion: string
  loadingRegion: boolean
  loadingTenant: boolean
  blueprintType: BlueprintTypes
  virtualNetworking: boolean
  underlayNetwork: {
    type: string
    label: string
    ranges: string
  }
  domain: {
    id: string
    name: string
  }
  tokenData: {
    expiresAt: string
    issuedAt: string
  }
  jwtToken: string
  activeTenant: string
  activeKaapiTenant: string
  kaapiTenantList: Array<string>
  isSingleRegionDeployment: boolean
  vmsLastPolledTimestamp: string
  volumesLastPolledTimestamp: string
  availableVolumesLastPolledTimestamp?: string
  nonPaginatedVolumesLastPolledTimestamp?: string
  // Pagination metadata for VMs
  vmsPagination?: {
    currentMarker?: string | null
    nextMarker?: string | null
    hasMore?: boolean
    pageSize?: number
    totalCount?: number
  }
  allVmsPagination?: {
    currentMarker?: string | null
    nextMarker?: string | null
    hasMore?: boolean
    pageSize?: number
    totalCount?: number
  }
  selfVmsPagination?: {
    currentMarker?: string | null
    nextMarker?: string | null
    hasMore?: boolean
    pageSize?: number
    totalCount?: number
  }
  // Page size preferences (persisted across sessions)
  vmsPageSize?: number
  hostVmsPageSize?: number
  // Search and sort preferences for VMs
  vmsSearchParams?: Record<string, string> | null
  vmsSortKey?: string
  vmsSortDir?: 'asc' | 'desc'
  // Cached params to track what was used to populate each cache
  vmsCachedParams?: {
    searchParams: Record<string, string> | null
    sortKey: string
    sortDir: string
    pageSize: number
  }
  allVmsCachedParams?: {
    searchParams: Record<string, string> | null
    sortKey: string
    sortDir: string
    pageSize: number
  }
  selfVmsCachedParams?: {
    searchParams: Record<string, string> | null
    sortKey: string
    sortDir: string
    pageSize: number
  }
  // Host-specific VM cache keys (for HostVmsListPage)
  hostVmsSearchParams?: Record<string, string> | null
  hostVmsSortKey?: string
  hostVmsSortDir?: 'asc' | 'desc'
  hostVmsCachedParams?: {
    searchParams: Record<string, string> | null
    sortKey: string
    sortDir: string
    pageSize: number
  }
  hostAllVmsCachedParams?: {
    searchParams: Record<string, string> | null
    sortKey: string
    sortDir: string
    pageSize: number
  }
  hostSelfVmsCachedParams?: {
    searchParams: Record<string, string> | null
    sortKey: string
    sortDir: string
    pageSize: number
  }
  // View preferences for VM list page toggles (shared across VirtualMachinesListPage and HostVmsListPage)
  vmsAllTenants?: boolean
  vmsAllOwners?: boolean
  // View preference for the Networks list page "All Tenants" toggle (admin-only)
  networksAllTenants?: boolean
  // View preference for the Volumes list page "All Tenants" toggle (admin-only)
  volumesAllTenants?: boolean
  // Track last visited VM list page to avoid unnecessary reloads
  lastVisitedVmPage?: 'vmList' | 'hostList' | null
  dex_client_secret: string
  showSessionExpiryWarning: boolean
  sessionExpiryWarningDismissed: boolean
  // Generic pagination metadata storage - keyed by dataKey or entityKey
  pagination?: Record<
    string,
    {
      currentMarker?: string | null
      nextMarker?: string | null
      hasMore?: boolean
      pageSize?: number
      totalCount?: number
    }
  >
  // Generic page size preferences - keyed by dataKey or entityKey
  paginationPageSizes?: Record<string, number>
  serviceCatalog?: Catalog[]
  noTenants?: boolean
}

export const initialState: SessionState = {
  unscopedToken: null,
  scopedToken: null,
  username: null,
  expiresAt: null,
  userDetails: {},
  features: {},
  isSsoToken: false,
  isSystemUser: false,
  onboardingRedirectToUrl: null,
  activeRegion: null,
  loadingRegion: true,
  loadingTenant: false,
  virtualNetworking: null,
  blueprintType: null,
  underlayNetwork: null,
  domain: null,
  tokenData: {
    expiresAt: null,
    issuedAt: null,
  },
  jwtToken: null,
  activeTenant: null,
  activeKaapiTenant: null,
  kaapiTenantList: [],
  isSingleRegionDeployment: false,
  vmsLastPolledTimestamp: null,
  volumesLastPolledTimestamp: null,
  availableVolumesLastPolledTimestamp: null,
  nonPaginatedVolumesLastPolledTimestamp: null,
  dex_client_secret: null,
  showSessionExpiryWarning: false,
  sessionExpiryWarningDismissed: false,
  serviceCatalog: [],
  noTenants: false,
}

const {
  name: sessionStoreKey,
  reducer: sessionReducers,
  actions: sessionActions,
} = createSlice({
  name: 'session',
  initialState,
  reducers: {
    initSession: (state, { payload }: PayloadAction<Partial<SessionState>>) => {
      return mergeLeft(payload, initialState)
    },
    updateSession: (state, { payload }: PayloadAction<Partial<SessionState>>) => {
      return mergeLeft(payload, state)
    },
    resetSession: (state, { payload }: PayloadAction<Partial<SessionState>>) => {
      return mergeLeft(
        {
          vmsLastPolledTimestamp: null,
          volumesLastPolledTimestamp: null,
          availableVolumesLastPolledTimestamp: null,
          nonPaginatedVolumesLastPolledTimestamp: null,
          vmsPagination: null,
          allVmsPagination: null,
          selfVmsPagination: null,
          vmsPageSize: null,
          vmsSearchParams: null,
          vmsSortKey: null,
          vmsSortDir: null,
          vmsCachedParams: null,
          allVmsCachedParams: null,
          selfVmsCachedParams: null,
          hostVmsPageSize: null,
          hostVmsSearchParams: null,
          hostVmsSortKey: null,
          hostVmsSortDir: null,
          hostVmsCachedParams: null,
          hostAllVmsCachedParams: null,
          hostSelfVmsCachedParams: null,
          vmsAllTenants: false,
          vmsAllOwners: false,
          networksAllTenants: false,
          volumesAllTenants: false,
          lastVisitedVmPage: null,
          pagination: null,
          paginationPageSizes: null,
          ...payload,
        },
        state,
      ) as SessionState
    },
    destroySession: () => {
      return initialState
    },
  },
})

export { sessionActions, sessionStoreKey }
export default sessionReducers
